import React from 'react';
import { X, FileCheck, Info, CircleAlert } from 'lucide-react';
import api from '../../utils/api';
import './OnboardingWalkthrough.css';

/**
 * OnboardingWalkthrough -- first-login modal that walks the user through
 * what they need to know based on their role and employment type.
 *
 * Variants:
 *   - Staff / Remote (contractor): full PTO flow
 *   - Staff / W2 (full-time): external PTO, log-for-visibility
 *   - Manager / Remote: staff content + approval queue + tools
 *   - Manager / W2: same as manager/remote but with W2 callout
 *   - Admin: setup-focused (departments, holidays, allocations, users)
 *
 * Props:
 *   - user: current user object from AuthContext
 *   - onDismiss: callback after dismissing (parent should update user state)
 */

// Content definitions keyed by checklist item ID
const ITEMS = {
  verify_profile: {
    step: true,
    title: 'Verify your information',
    desc: 'Your admin has set up your profile. If your country, department, or birthday looks wrong, let your admin know.',
  },
  check_balance: {
    step: true,
    title: 'Check your PTO balance',
    desc: 'Your dashboard shows your current allocation and how many days you have remaining. PTO resets at the start of each calendar year.',
  },
  browse_calendar: {
    step: true,
    title: 'Browse the holiday calendar',
    desc: "See which holidays are observed for your country. Holidays don't count against your PTO balance.",
  },
  submit_pto: {
    step: true,
    title: 'Submit time off when you\'re ready',
    desc: 'Go to Time Off and click "Request Time Off." Select your dates, choose full or half days, and submit. Your manager will review it.',
  },
  birthday_info: {
    step: false,
    title: 'Birthday day off',
    desc: "You get one extra day off during your birth month -- it doesn't come out of your PTO. Use it any day that month.",
  },

  // W2-specific
  w2_log_time: {
    step: true,
    title: 'Log your time off for team visibility',
    desc: "When you take time off, submit it here so it shows on the team calendar. There's no approval step -- your entries are recorded automatically.",
  },
  w2_calendar: {
    step: true,
    title: 'Browse the holiday calendar',
    desc: "See which holidays are observed for your country. Holidays don't count as time off.",
  },
  w2_birthday: {
    step: false,
    title: 'Birthday day off',
    desc: "You get one extra day off during your birth month. Submit it here the same way -- it's tracked separately from your external PTO.",
  },

  // Manager-specific
  approval_queue: {
    step: true,
    title: 'Review the approval queue',
    desc: 'When someone on your team submits a request, it appears in Approvals. You can approve, deny (with a reason), or remove requests for your department.',
  },
  manager_notifications: {
    step: false,
    title: "You'll get email notifications",
    desc: "When a team member submits a request, you'll receive an email. If a request goes unaddressed for 2 business days, you'll get a reminder.",
  },
  manage_tools: {
    step: true,
    title: 'Manage tools in Apps & Tools',
    desc: 'You can add and edit tools in the company directory. Check existing categories before creating new ones.',
  },

  // Admin-specific
  setup_departments: {
    step: true,
    title: 'Set up departments',
    desc: 'Create your team structure in Admin > Departments. Assign managers and team members to each department.',
  },
  setup_holidays: {
    step: true,
    title: 'Configure holiday calendars',
    desc: 'In Admin > Holidays, select which national holidays are observed for each country. You can observe up to 9 per country.',
  },
  setup_allocations: {
    step: true,
    title: 'Set PTO allocations',
    desc: "In Admin > Allocations, set each contractor's annual PTO days. The system can suggest prorated amounts for mid-year hires.",
  },
  activate_users: {
    step: true,
    title: 'Activate team members',
    desc: 'New users start in "Pending Setup." Once their department, country, birthday, and employment type are configured, activate them in Admin > Users.',
  },
  admin_approvals: {
    step: false,
    title: 'Approve requests and manage time off',
    desc: 'You can approve or deny any request, submit time off on behalf of others, award bonus PTO days, and retroactively remove requests if needed.',
  },
  admin_w2_info: {
    step: false,
    title: 'W2 vs. remote employees',
    desc: "W2 (full-time) employees have PTO tracked externally. Their time off entries auto-approve and don't draw from a balance. Contractors use the internal PTO tracker with allocations you set.",
  },
  admin_settings: {
    step: false,
    title: 'App settings',
    desc: 'Customize the app name, login page text, and branding in Admin > Settings. Manage featured tools on the dashboard and configure blackout dates for busy periods.',
  },
};

/**
 * Get the walkthrough content sections based on role and employment type.
 */
function getContent(role, employmentType) {
  const isW2 = employmentType === 'staff_w2';

  if (role === 'admin') {
    return {
      subtitle: "You have admin access. Here's how to set things up.",
      badge: 'Administrator',
      callout: null,
      sections: [
        {
          label: 'Initial setup',
          items: ['setup_departments', 'setup_holidays', 'setup_allocations', 'activate_users'],
        },
        {
          label: 'Ongoing management',
          items: ['admin_approvals', 'admin_w2_info', 'admin_settings'],
        },
      ],
    };
  }

  if (role === 'manager') {
    const timeOffItems = isW2
      ? ['w2_log_time', 'w2_calendar', 'w2_birthday']
      : ['check_balance', 'submit_pto', 'birthday_info'];

    return {
      subtitle: "Here's everything you need to get started.",
      badge: 'Manager',
      callout: isW2
        ? "Your PTO is managed externally through HR. You'll still log time off here so your team knows when you're out -- requests are approved automatically."
        : null,
      sections: [
        { label: 'Your profile', items: ['verify_profile'] },
        { label: 'Your time off', items: timeOffItems },
        {
          label: 'Managing your team',
          items: ['approval_queue', 'manager_notifications', 'manage_tools'],
        },
      ],
    };
  }

  // Staff (default)
  if (isW2) {
    return {
      subtitle: "Here's everything you need to get started.",
      badge: 'Team Member',
      callout: "Your PTO is managed externally through HR. You'll still log time off here so your team knows when you're out -- requests are approved automatically.",
      sections: [
        { label: 'Your profile', items: ['verify_profile'] },
        { label: 'Time Off', items: ['w2_log_time', 'w2_calendar', 'w2_birthday'] },
      ],
    };
  }

  return {
    subtitle: "Here's everything you need to get started.",
    badge: 'Team Member',
    callout: null,
    sections: [
      { label: 'Your profile', items: ['verify_profile'] },
      { label: 'Time Off', items: ['check_balance', 'browse_calendar', 'submit_pto', 'birthday_info'] },
    ],
  };
}

function OnboardingWalkthrough({ user, onDismiss }) {
  const content = getContent(user.role, user.employment_type);
  const firstName = user.first_name || user.name?.split(' ')[0] || 'there';

  // Global step counter across all sections
  let stepNum = 0;

  const handleDismiss = async () => {
    try {
      await api.post('/users/me/onboarding/dismiss');
    } catch {
      // Dismiss locally even if the API call fails
    }
    onDismiss();
  };

  return (
    <div className="onboarding-overlay" onClick={handleDismiss}>
      <div className="onboarding-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="onboarding-header">
          <button className="onboarding-dismiss" onClick={handleDismiss}>
            <X size={18} />
          </button>
          <div className="onboarding-icon">
            <FileCheck size={24} strokeWidth={1.5} />
          </div>
          <h2 className="onboarding-title">Welcome to FPS OS, {firstName}!</h2>
          <p className="onboarding-subtitle">{content.subtitle}</p>
          <span className="onboarding-badge">{content.badge}</span>
        </div>

        {/* Body */}
        <div className="onboarding-body">
          {/* W2 callout */}
          {content.callout && (
            <div className="onboarding-callout">
              <CircleAlert size={14} className="onboarding-callout-icon" />
              <span>{content.callout}</span>
            </div>
          )}

          {content.sections.map((section, si) => (
            <div key={si}>
              <div className="onboarding-section-label">{section.label}</div>
              <ul className="onboarding-checklist">
                {section.items.map((itemKey) => {
                  const item = ITEMS[itemKey];
                  if (!item) return null;
                  if (item.step) stepNum++;

                  return (
                    <li key={itemKey} className="onboarding-item">
                      <span className={`onboarding-item-icon ${item.step ? 'step' : 'info'}`}>
                        {item.step ? stepNum : <Info size={12} strokeWidth={2.5} />}
                      </span>
                      <div>
                        <span className="onboarding-item-title">{item.title}</span>
                        <span className="onboarding-item-desc">{item.desc}</span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="onboarding-footer">
          <button className="onboarding-cta" onClick={handleDismiss}>
            Got it, let's go
          </button>
          <p className="onboarding-footer-hint">
            You can revisit this anytime from the Help page.
          </p>
        </div>
      </div>
    </div>
  );
}

export default OnboardingWalkthrough;
